// src/services/fleetApi.ts — Fleet GPS tracking API calls (separate from fertilizer api.ts).
import axios from 'axios';
import type {
  FleetPermission, FleetRole, User, ISODate,
  FleetSourceStatus, FleetStreamMessage, Trip, TripAlert, TripLocation, Vehicle,
} from '../types';

const BASE = process.env.REACT_APP_API_URL || '';   // '' = same origin (relative)

const api = axios.create({ baseURL: BASE });

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/fleet/login';
    }
    return Promise.reject(err);
  }
);

type Params = Record<string, string | number | boolean | undefined>;
type Ok<T> = { success: boolean } & T;
/** Fields a form may send when creating/editing — strings from inputs are fine. */
type FormBody = Record<string, string | number | boolean | null | undefined>;

// ── Dashboard / map ───────────────────────────────────────
export interface FleetDashboardStats {
  totalVehicles: number;
  /** Trucks on the live map: those with a PENDING or ACTIVE trip. */
  trackingNow: number;
  activeTrips: number;
  idleVehicles: number;
  todayCompletedTrips: number;
  totalBagsInTransit: number;
  /** Trips created but the truck has not left the plant yet. */
  waitingTrips: number;
  waitingLongTrips: number;
  unresolvedAlerts: number;
  offlineVehicles: number;
  delayedVehicles: number;
  gpsGapVehicles: number;
  duplicateDataAlerts: number;
  wrongCoordinateAlerts: number;
  networkFailures: number;
  unhealthySources: number;
}
export const getFleetDashboard = () => api.get<Ok<{
  stats: FleetDashboardStats;
  issueBreakdown: Record<string, number>;
  sourceStatuses: FleetSourceStatus[];
  vehicles: Vehicle[];
}>>('/api/fleet/dashboard');

// ── Vehicles ──────────────────────────────────────────────
export const getVehicles   = () => api.get<Ok<{ vehicles: Vehicle[] }>>('/api/fleet/vehicles');
export const addVehicle    = (data: FormBody) => api.post<Ok<{ vehicle: Vehicle }>>('/api/fleet/vehicles', data);
export const getVehicle    = (id: string) => api.get<Ok<{ vehicle: Vehicle & { trips?: Trip[] } }>>(`/api/fleet/vehicles/${id}`);
export const updateVehicle = (id: string, data: FormBody) => api.put<Ok<{ vehicle: Vehicle }>>(`/api/fleet/vehicles/${id}`, data);

// ── Trips ─────────────────────────────────────────────────
export const getTrips     = (params?: Params) => api.get<Ok<{ trips: Trip[] }>>('/api/fleet/trips', { params });
export const getTrip      = (id: string) => api.get<Ok<{ trip: Trip; lastLocations: TripLocation[] }>>(`/api/fleet/trips/${id}`);
export const createTrip   = (data: FormBody) => api.post<Ok<{ trip: Trip }>>('/api/fleet/trips', data);
export const updateTrip   = (id: string, data: FormBody) => api.put<Ok<{ trip: Trip }>>(`/api/fleet/trips/${id}`, data);
export const startTrip    = (id: string) => api.patch<Ok<{ trip: Trip }>>(`/api/fleet/trips/${id}/start`);
export const completeTrip = (id: string, data?: { ackQuantityMt?: number }) => api.patch<Ok<{ trip: Trip }>>(`/api/fleet/trips/${id}/complete`, data || {});
export const cancelTrip   = (id: string, data: { reason?: string }) => api.patch<Ok<{ trip: Trip }>>(`/api/fleet/trips/${id}/cancel`, data);
// Trip Details page (backend fleetController.getTripDetails)
export type IfmsApi = 'TRIP_START' | 'TRIP_END' | 'TRIP_CLOSURE';
export interface TripMetrics {
  estimatedKm: number | null; estimatedMin: number | null; actualKm: number;
  /**
   * actualKm split into what the GPS trail actually showed and what was drawn
   * straight across a period when the device said nothing. They sum to actualKm.
   * `completeness` is observed ÷ actual, 0..1 — 1 means an unbroken trail.
   */
  observedKm?: number; bridgedKm?: number; completeness?: number;
  gapCount?: number; gapMin?: number; longestGapMin?: number;
  runningMin: number; haltMin: number; elapsedMin: number | null;
  /** How much further the truck has to go, and what that figure is measured along. */
  remainingKm: number | null; remainingBasis: string | null;
  /** Time between creating the trip and the truck actually leaving the plant. */
  waitingMin: number; departed: boolean;
  /** + = taking longer than the road-time estimate */
  differentialMin: number | null;
  deviationKm: number; maxDeviationKm: number; diversions: number;
  stops: number; unauthorizedHalts: number; offRouteNow: boolean;
  roadDeviationKm: number; stopMin: number; unauthorizedHaltMin: number; points: number; gpsDisconnections: number;
}
/** One stretch the truck drove off the planned road route. */
export interface TripDeviation {
  index: number; points: LatLngTuple[]; km: number; maxKm: number; minutes: number;
  startAt: ISODate; endAt: ISODate; latitude: number; longitude: number;
}
export interface TripStop { startAt: ISODate; endAt: ISODate; minutes: number; latitude: number; longitude: number; unauthorized: boolean }
export interface TripActivityRow { id: string; action: string; message: string; actor: string | null; createdAt: ISODate }
export interface IfmsLogRow {
  id: string; api: IfmsApi; label: string; status: 'SUCCESS' | 'FAILED' | 'NOT_SENT';
  httpStatus: number | null; remarks: string; attempts: number; createdAt: ISODate; updatedAt: ISODate;
}
/** One dealer-wise consignment line (IFMS dispatchDetails[] / receiptDetails[]). */
export interface TripDealerRow {
  id: string;
  dispatchToType: 'RETAILER' | 'WHOLESALER' | 'RAKEPOINT' | string;
  dispatchToCode: string;
  dealerName: string | null;
  destStateCode: string;
  destDistrictCode: string;
  productCode: string;
  proposedDistanceKm: number | null;
  quantityMt: number;
  quantityAckMt: number | null;
  dispatchReceiptId: string | null;
}
export type TripDealerBody = Record<string, string | number | null | undefined>;
export const addTripDealer    = (id: string, data: TripDealerBody) => api.post<Ok<{ dealer: TripDealerRow }>>(`/api/fleet/trips/${id}/dealers`, data);
export const updateTripDealer = (id: string, lineId: string, data: TripDealerBody) => api.put<Ok<{ dealer: TripDealerRow }>>(`/api/fleet/trips/${id}/dealers/${lineId}`, data);
export const deleteTripDealer = (id: string, lineId: string) => api.delete<Ok<object>>(`/api/fleet/trips/${id}/dealers/${lineId}`);

// IFMS outbox — what has not reached IFMS yet (backend fleetController.getIfmsQueue)
export interface IfmsQueueItem {
  id: string; tripId: string; tripNumber: string; vehicleNumber: string; tripStatus: string;
  /** Destination region of the trip and why this call is stuck. */
  region: string; regionPlace: string; stateCode: string | null; districtCode: string | null;
  reasonCode: string; reason: string;
  /**
   * SUCCESS           accepted by IFMS
   * RETRYING          a temporary failure; it goes again on its own
   * UNKNOWN_OUTCOME   the request may have arrived — a person must check
   * ON_HOLD           data, credentials or permission need fixing first
   * NOT_SENT          waiting for the call before it, or reporting is not on yet
   * FAILED / QUEUED   written by rows from before the queue was reworked
   */
  api: IfmsApi; label: string;
  status: 'SUCCESS' | 'RETRYING' | 'UNKNOWN_OUTCOME' | 'ON_HOLD' | 'PROCESSING' | 'NOT_SENT' | 'FAILED' | 'QUEUED';
  attempts: number; remarks: string; missing: string[];
  holdReason?: string | null; errorClass?: string | null; reconcileState?: string | null;
  createdAt: ISODate; lastAttemptAt: ISODate | null; nextRetryAt: ISODate | null; dueAt: ISODate;
  hoursLeft: number; overdue: boolean; escalated: boolean;
}
export interface IfmsQueueSummary {
  pending: number; overdue: number; dueSoon: number; blocked: number; delivered: number;
  slaHours: number; configured: boolean;
}
/** How each destination region is doing with IFMS. */
export interface IfmsRegionRow {
  key: string; stateCode: string | null; districtCode: string | null; place: string;
  trips: number; delivered: number; pending: number; overdue: number; blocked: number;
  successPct: number; reason: string | null;
}
export interface IfmsReasonRow { code: string; label: string; count: number; overdue: number; regions: string[] }
export const getIfmsQueue = () =>
  api.get<Ok<{ items: IfmsQueueItem[]; regions: IfmsRegionRow[]; reasons: IfmsReasonRow[]; summary: IfmsQueueSummary }>>('/api/fleet/ifms/queue');
/** One fix the server refused, with why. */
export interface GpsRejection {
  id: string; latitude: number; longitude: number; speed: number | null;
  deviceTime: ISODate | null; receivedAt: ISODate;
  reason: string; detail: string | null;
  impliedKmh: number | null; jumpKm: number | null; gapSeconds: number | null;
  confidence: string;
}
/** Why a truck looks quiet: the fixes that arrived and were not kept. */
export const getVehicleRejections = (vehicleId: string, hours = 24) =>
  api.get<Ok<{ hours: number; total: number; byReason: Record<string, number>; rejections: GpsRejection[] }>>(
    `/api/fleet/vehicles/${vehicleId}/rejections?hours=${hours}`);

export const runIfmsQueue = () => api.post<Ok<{ sent: number; failed: number; escalated: number; checked: number }>>('/api/fleet/ifms/run');

/** One attempt on IFMS, as recorded at the time. */
export interface IfmsAttempt {
  attempt: number; startedAt: ISODate; durationMs: number | null;
  httpStatus: number | null; outcome: string; error: string | null; correlationId: string | null;
}
export interface IfmsEvent {
  tripId: string; api: IfmsApi; label: string; tripNumber: string; vehicleNumber: string | null;
  status: IfmsQueueItem['status']; errorClass: string | null;
  holdReason: string | null; reconcileState: string | null; reconcileNote: string | null;
  attempts: number; firstAttemptAt: ISODate | null; lastAttemptAt: ISODate | null;
  nextRetryAt: ISODate | null; ackAt: ISODate | null; ackRef: string | null;
  idempotencyKey: string | null; remarks: string | null; logId: string | null;
  missing: string[]; payload: Record<string, unknown> | null; history: IfmsAttempt[];
}
export const getIfmsEvent = (tripId: string, apiName: IfmsApi) =>
  api.get<Ok<{ event: IfmsEvent }>>(`/api/fleet/ifms/event/${tripId}/${apiName}`);
/** Close out a report whose outcome was never confirmed. The note is the evidence. */
export const reconcileIfms = (logId: string, accepted: boolean, note: string) =>
  api.post<Ok<{ log: unknown }>>(`/api/fleet/ifms/reconcile/${logId}`, { accepted, note });

export interface TripDetails {
  trip: Trip; dealers: TripDealerRow[]; metrics: TripMetrics; stops: TripStop[]; deviations: TripDeviation[]; roadPath: LatLngTuple[] | null;
  locations: TripLocation[]; activities: TripActivityRow[]; ifmsLogs: IfmsLogRow[];
  ifms: { configured: boolean; state?: 'off' | 'sent' | 'pending' | 'attention'; missing: { TRIP_START: string[] } };
}
export const getTripDetails = (id: string) => api.get<Ok<TripDetails>>(`/api/fleet/trips/${id}/details`);
export const sendIfms       = (id: string, apiName: IfmsApi) => api.post<Ok<{ log: IfmsLogRow }>>(`/api/fleet/trips/${id}/ifms/${apiName}`);
export const getTripRoute = (id: string) => api.get<Ok<{ count: number; locations: TripLocation[] }>>(`/api/fleet/trips/${id}/route`);

// ── Alerts ────────────────────────────────────────────────
export const getFleetAlerts    = (params?: Params) => api.get<Ok<{ alerts: TripAlert[] }>>('/api/fleet/alerts', { params });
export const resolveFleetAlert = (id: string) => api.patch<Ok<{ alert: TripAlert }>>(`/api/fleet/alerts/${id}/resolve`);

// ── Vendor / dealer master ────────────────────────────────
export interface Vendor {
  id: string; code: string; name: string; type: string;
  stateCode: string | null; stateName: string | null;
  districtCode: string | null; districtName: string | null;
  address: string | null; mobile: string | null;
  latitude: number | null; longitude: number | null;
  dispatchCode: string | null; isActive: boolean;
  /** OK | FIXED | APPROX | SUSPECT | MISSING | UNRESOLVED — see scripts/verifyVendorGeo.js */
  geoStatus: string | null; geoNote: string | null;
}
export interface VendorImportResult {
  dryRun: boolean; fileRows: number; accepted: number; created: number; updated: number; skipped: number;
  /** Sheet row the headings were found on (title rows above it are skipped). */
  headerRow: number;
  columns: Record<string, string>;
  /** Columns we do not have a field for — kept with the dealer as extra detail. */
  ignoredColumns: string[];
  problems: string[]; total: number;
}
// ── LGD state / district reference lists ─────────────────────────────────────
// The government's own codes, which IFMS expects as destinationStateCode and
// destinationDistrictCode. Served from the backend so there is one copy.
export interface LgdState { code: string; name: string; short: string; ut: boolean }
export interface LgdDistrict { code: string; stateCode: string; name: string }
export const getLgd = () =>
  api.get<Ok<{ states: LgdState[]; districts: LgdDistrict[] }>>('/api/fleet/masters/lgd');

export const getVendors = (p?: Params) =>
  api.get<Ok<{ rows: Vendor[]; total: number; page: number; limit: number; pages: number }>>('/api/fleet/vendors', { params: p });
export const saveVendor   = (data: FormBody) => api.post<Ok<{ vendor: Vendor }>>('/api/fleet/vendors', data);
export const updateVendor = (id: string, data: FormBody) => api.put<Ok<{ vendor: Vendor }>>(`/api/fleet/vendors/${id}`, data);
/** Upload the dealer list; `dryRun` checks the file without saving anything. */
export const importVendors = (file: File, dryRun = false) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post<Ok<VendorImportResult>>(`/api/fleet/vendors/import${dryRun ? '?dryRun=1' : ''}`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const vendorTemplate = () => api.get<Blob>('/api/fleet/vendors/template', { responseType: 'blob' });

// ── Reports (shape declared by the reports page) ──────────
export const getFleetReports = <T = unknown>() => api.get<Ok<T>>('/api/fleet/reports');

// Report modules — rows as returned by backend/src/controllers/fleetReportController.js
export interface TripReportRow {
  id: string; tripNumber: string; vehicleNumber: string; driverName: string;
  fromLocation: string; toLocation: string; status: string;
  dispatchType: string | null; challanCode: string | null; invoiceNumber: string | null;
  quantityMt: number | null; bagsCount: number;
  createdAt: ISODate; startedAt: ISODate | null; endedAt: ISODate | null;
  durationMin: number | null; plannedKm: number; distanceKm: number; alerts: number;
}
export interface VehicleSummaryRow {
  id: string; vehicleNumber: string; driverName: string; trips: number; completedTrips: number;
  distanceKm: number; runningMin: number; haltMin: number; maxSpeed: number; avgSpeed: number; pings: number;
}
export interface StoppageRow {
  id: string; vehicleNumber: string; driverName: string; tripId: string; tripNumber: string;
  startAt: ISODate; endAt: ISODate; durationMin: number; latitude: number; longitude: number;
}
export interface OverspeedRow extends StoppageRow { maxSpeed: number; avgSpeed: number }
export interface AlertReportRow {
  id: string; createdAt: ISODate; type: string; severity: string; message: string;
  vehicleNumber: string; tripNumber: string; tripId: string | null; source: string;
  status: 'Open' | 'Resolved'; resolvedAt: ISODate | null; latitude: number | null; longitude: number | null;
}
export interface DeviceHealthRow {
  id: string; vehicleNumber: string; driverName: string; gpsDeviceId: string; tripStatus: string;
  deviceImei: string; vendorName: string; vendorCode: string; devicePowerOn: boolean | null;
  healthStatus: string; healthLabel: string; lastGpsAt: ISODate | null; lastPingAt: ISODate | null;
  gpsAgeMinutes: number | null; dataDelayMinutes: number | null; speed: number;
  latitude: number | null; longitude: number | null; openIssues: number; lastIssue: string; active: boolean;
}
export type TimeVerdict = 'ON_TIME' | 'EARLY' | 'LATE' | 'RUNNING_LATE' | 'ON_TRACK' | 'NOT_STARTED' | 'NOT_STARTED_LATE' | 'NO_ESTIMATE' | 'CANCELLED';
export interface TripPerformanceRow {
  id: string; tripNumber: string; vehicleNumber: string; fromLocation: string; toLocation: string; status: string;
  estStart: ISODate | null; estEnd: ISODate | null; startedAt: ISODate | null; completedAt: ISODate | null;
  estimatedMin: number | null; actualMin: number | null;
  /** + = took longer than planned (for a running trip: time so far vs plan) */
  extraMin: number | null;
  /** + = reached after the estimated arrival */
  arrivalDelayMin: number | null;
  verdict: TimeVerdict;
  deviations: number;
  /** km driven off the planned route (null while the trip is still running) */
  deviationKm: number | null;
  deviationMin: number | null; maxDeviationKm: number | null; deviationOpen: boolean;
}
export interface TripPerformanceSummary {
  trips: number;
  deviationTrips: number; deviationEvents: number; deviationMin: number; maxDeviationKm: number; deviatingNow: number; deviationThresholdKm: number;
  withEstimate: number; onTime: number; finished: number; onTimePct: number | null; late: number; runningLate: number;
  avgExtraMin: number | null; totalEstimatedMin: number; totalActualMin: number; graceMin: number;
}
export const getTripPerformance = (p?: Params) =>
  api.get<Ok<{ rows: TripPerformanceRow[]; summary: TripPerformanceSummary }>>('/api/fleet/reports/trip-performance', { params: p });
export const getTripReport      = (p?: Params) => api.get<Ok<{ rows: TripReportRow[]; truncated?: boolean }>>('/api/fleet/reports/trips', { params: p });
export const getVehicleSummary  = (p?: Params) => api.get<Ok<{ rows: VehicleSummaryRow[] }>>('/api/fleet/reports/vehicles', { params: p });
export const getStoppageReport  = (p?: Params) => api.get<Ok<{ rows: StoppageRow[] }>>('/api/fleet/reports/stoppages', { params: p });
export const getOverspeedReport = (p?: Params) => api.get<Ok<{ rows: OverspeedRow[] }>>('/api/fleet/reports/overspeed', { params: p });
export const getAlertReport     = (p?: Params) => api.get<Ok<{ rows: AlertReportRow[]; types: string[] }>>('/api/fleet/reports/alerts', { params: p });
export const getDeviceHealth    = (p?: Params) => api.get<Ok<{ rows: DeviceHealthRow[]; sources: FleetSourceStatus[] }>>('/api/fleet/reports/device-health', { params: p });
/** Server builds the .xlsx from exactly the rows/columns on screen. */
export const exportReportXlsx = (body: { title: string; subtitle?: string; columns: { key: string; label: string }[]; rows: Record<string, unknown>[] }) =>
  api.post<Blob>('/api/fleet/reports/export', body, { responseType: 'blob' });

// ── Administration (Roles & Users) ────────────────────────
export interface RoleBody { name: string; permissions: FleetPermission[] }
export interface UserBody { name: string; email: string; password?: string; roleIds?: string[]; isActive?: boolean }
export const getRoles   = () => api.get<Ok<{ roles: FleetRole[]; permissions: FleetPermission[] }>>('/api/fleet/admin/roles');
export const createRole = (data: RoleBody) => api.post<Ok<{ role: FleetRole }>>('/api/fleet/admin/roles', data);
export const updateRole = (id: string, data: RoleBody) => api.put<Ok<{ role: FleetRole }>>(`/api/fleet/admin/roles/${id}`, data);
export const deleteRole = (id: string) => api.delete<Ok<object>>(`/api/fleet/admin/roles/${id}`);
export const getAdminUsers = () => api.get<Ok<{ users: User[] }>>('/api/fleet/admin/users');
export const createUser    = (data: UserBody) => api.post<Ok<{ user: User }>>('/api/fleet/admin/users', data);
export const updateUser    = (id: string, data: UserBody) => api.put<Ok<{ user: User }>>(`/api/fleet/admin/users/${id}`, data);
export const deleteUser    = (id: string) => api.delete<Ok<{ deactivated?: boolean }>>(`/api/fleet/admin/users/${id}`);

/** Administration → IFMS. Status is read-only; the credentials live in the server's .env. */
export interface IfmsApiRow { api: string; label: string; path: string; success: number; pending: number }
export interface IfmsStatus {
  configured: boolean; reportingStarted: boolean; environment: string;
  baseUrl: string | null; clientId: string | null; secretSet: boolean;
  reportingFrom: string | null; slaHours: number;
  apis: IfmsApiRow[]; tripsLastWeek: number; escalated: number;
}
export interface IfmsTestResult { ok: boolean; step: string; message: string; httpStatus?: number; ms?: number }
export const getIfmsStatus = () => api.get<Ok<{ ifms: IfmsStatus }>>('/api/fleet/admin/ifms');

/** Dashboard assistant — read-only questions over the live fleet data. */
export interface AiTurn { role: 'user' | 'assistant'; content: string }
export const askFleetAi = (question: string, history: AiTurn[]) =>
  api.post<Ok<{ answer: string; mode: 'live' | 'offline'; model?: string; note?: string }>>(
    '/api/fleet/ai/ask', { question, history });
export const testIfms      = () => api.post<Ok<{ result: IfmsTestResult }>>('/api/fleet/admin/ifms/test');

/** Administration → GPS Providers. */
export interface GpsProvider {
  id: string; name: string; auth: string; note: string | null;
  editableBaseUrl: boolean; baseUrl: string | null; configured: boolean;
  trucks: number; whitelisted: number;
  status: string; lastPollAt: string | null; lastSuccessAt: string | null; lastError: string | null;
}
export interface ProviderTestResult { ok: boolean; message: string; ms?: number; httpStatus?: number }
export const getProviders   = () => api.get<Ok<{ providers: GpsProvider[] }>>('/api/fleet/admin/providers');
export const setProviderUrl = (id: string, baseUrl: string) =>
  api.put<Ok<{ baseUrl: string | null }>>(`/api/fleet/admin/providers/${id}/base-url`, { baseUrl });
export const testProvider   = (id: string) =>
  api.post<Ok<{ result: ProviderTestResult }>>(`/api/fleet/admin/providers/${id}/test`);

// ── Road geometry → real road path, not a straight line ──────────────────────
//
// This used to call router.project-osrm.org straight from the browser. It now
// asks our own backend, which holds the routing engine. The reasons are in
// fleetController.getRoadPath; the one that matters here is that our OSRM is
// bound to localhost and a browser cannot reach it.
//
// The fallback is unchanged and deliberate: null means "draw a straight line",
// not "fail". A map that will not load because a road could not be found is
// worse than a map with one straight line on it.
export type LatLngTuple = [number, number];

export async function getRoadPath(fromLat: number, fromLng: number, toLat: number, toLng: number): Promise<LatLngTuple[] | null> {
  try {
    const { data } = await api.get<Ok<{ route: { path: LatLngTuple[] } | null }>>('/api/fleet/road-path', {
      params: { fromLat, fromLng, toLat, toLng },
    });
    const path = data?.route?.path;
    if (path && path.length > 1) return path;
  } catch { /* fall back to straight line */ }
  return null;
}

// ── India bounds (rough box covering the whole country) ───────────────────────
export const INDIA_BOUNDS = { latMin: 6.5, latMax: 37.5, lngMin: 68.0, lngMax: 97.5 };
export function isInIndia(lat: number | string | null | undefined, lng: number | string | null | undefined): boolean {
  const a = parseFloat(String(lat)), o = parseFloat(String(lng));
  if (Number.isNaN(a) || Number.isNaN(o)) return false;
  return a >= INDIA_BOUNDS.latMin && a <= INDIA_BOUNDS.latMax &&
         o >= INDIA_BOUNDS.lngMin && o <= INDIA_BOUNDS.lngMax;
}

// ── Geocode an address → {lat, lng} via OSM Nominatim (India only) ────────────
// Lets the operator type a place name and auto-fill correct coordinates,
// instead of typing raw lat/lng by hand (which caused junk points abroad).
interface NominatimHit { lat: string; lon: string }
export async function geocodeAddress(query: string | null | undefined): Promise<{ lat: string; lng: string } | null> {
  const q = (query || '').trim();
  if (!q) return null;
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=in&q=${encodeURIComponent(q)}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    const data: NominatimHit[] = await res.json();
    if (Array.isArray(data) && data[0]) {
      const lat = parseFloat(data[0].lat), lng = parseFloat(data[0].lon);
      if (isInIndia(lat, lng)) return { lat: lat.toFixed(5), lng: lng.toFixed(5) };
    }
  } catch { /* ignore — caller shows a message */ }
  return null;
}

// ── Live location stream (SSE) ────────────────────────────
// Returns an unsubscribe function. onMessage receives each parsed event.
export const subscribeToStream = (onMessage: (msg: FleetStreamMessage) => void): (() => void) => {
  const es = new EventSource(`${BASE}/api/fleet/stream`);
  es.onmessage = (e: MessageEvent<string>) => {
    try { onMessage(JSON.parse(e.data) as FleetStreamMessage); } catch { /* keep-alive/comment */ }
  };
  es.onerror = () => { /* browser auto-reconnects */ };
  return () => es.close();
};

export default api;
